/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DeviceType, useDevice } from '@squiz/dxp-console-support';
import { NavigationSection, SideNav } from '@squiz/sds';
import { useConsoleServiceStore, useDxpFrameStore } from 'dxpConsoleCore-mf/State';
import React, { useEffect } from 'react';
import { DXP_ID_MAIN_NAVIGATION } from '../../../../Constants/ContentIds';
import { DxpCapability } from '../../../../Constants/DxpCapabilities/defs/DxpCapability.defs';
import { getTenantFromUrl } from '../../../../Utilities/GetTenantFromUrl/GetTenantFromUrl';
import { processNavItems } from '../../../../Utilities/ProcessNavItems/ProcessNavItems';

/**
 * DXP Console Frame menu.
 */
export const FrameMenu = (): JSX.Element => {
  // Device detection
  const deviceType = useDevice();
  const isDesktop = deviceType === DeviceType.desktop;

  // Frame settings
  const { showSideNavigation, updateNavigationSections } = useDxpFrameStore();
  const {
    capability,
    navigationSections,
  }: {
    capability: DxpCapability | null;
    navigationSections: Array<NavigationSection>;
  } = useDxpFrameStore();
  const { tenant } = useConsoleServiceStore();

  useEffect(() => {
    // If a capability exists, the menu should be updated. This is also
    // scoped so only capability updates will force an update.
    if (capability) {
      // On page reload, the `tenant` object from state is not available
      // In that case, get the tenantReferenceId from the URL instead
      const tenantReferenceId = tenant ? tenant.tenantReferenceId : getTenantFromUrl();

      const newSections = capability.menuSections
        .map((section: NavigationSection) => {
          return {
            ...section,
            items: section.items
              .map((item) => processNavItems(tenantReferenceId, item))
              .filter((item) => item !== null),
            // Null values from the map function are filtered out but not recognised
            // by TypeScript. Apply type cast to fix TypeScript complaint.
          } as NavigationSection;
        })
        .filter((section: NavigationSection) => {
          // Hide the section if the user has no permissions for any of the navigation items.
          return section.items.length > 0;
        });

      updateNavigationSections(newSections);
    }
  }, [capability, tenant, updateNavigationSections]);

  return (
    <>
      {isDesktop && showSideNavigation && (
        <div className="sidebar" id={DXP_ID_MAIN_NAVIGATION} tabIndex={-1}>
          <SideNav sections={navigationSections} />
        </div>
      )}
    </>
  );
};
